import * as React from 'react';
import { TextInput } from 'sos-common/js/components/FormControls/TextInput';
import { RadioButtonGroup } from 'sos-common/js/components/FormControls/RadioButtonGroup';
import { RadioButton } from 'sos-common/js/components/FormControls/RadioButton';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import { SectionClass, SectionState, SectionProps, FieldName, FormType } from '../PublicForm.types';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import { MAX_LENGTH_NUM } from '../publicform.validation.service';

export interface IPersonuppgifterSectionProps extends SectionProps {}
export interface IPersonuppgifterSectionState extends SectionState {
  emailvalue: string;
}

const RequiredFieldList = [
  FieldName.AllaFornamn,
  FieldName.Tilltalsnamn,
  FieldName.Efternamn,
  FieldName.Personnummer,
  FieldName.Adress,
  FieldName.Postnummer,
  FieldName.Ort,
  FieldName.Land,
  FieldName.Telefon,
  FieldName.Epost,
  FieldName.EpostBekraftelse,
  FieldName.Kon,
];

const RequiredFieldListYrkesTitel = [
  FieldName.AllaFornamn,
  FieldName.Tilltalsnamn,
  FieldName.Efternamn,
  FieldName.samordningsnummerOrBirthDate,
  FieldName.Personnummer,
  FieldName.Land,
  FieldName.Epost,
  FieldName.EpostBekraftelse,
  FieldName.Kon,
];
const RequiredFieldListOutSideEuEesLessThanThreeYears = [
  FieldName.AllaFornamn,
  FieldName.Efternamn,
  FieldName.Personnummer,
  FieldName.Adress,
  FieldName.Postnummer,
  FieldName.Ort,
  FieldName.Land,
  FieldName.Telefon,
  FieldName.Epost,
  FieldName.EpostBekraftelse,
  FieldName.Kon,
];
export default class PersonuppgifterSection extends SectionClass<
  IPersonuppgifterSectionProps,
  IPersonuppgifterSectionState
> {
  private _bekraftaEpostRef: React.RefObject<TextInput>;
  private _samordningsNr: React.RefObject<TextInput>;
  private _personNr: React.RefObject<TextInput>;

  private _mandatoryString: string;
  private _oneFieldMandatory: string;
  constructor(props: IPersonuppgifterSectionProps) {
    super(props);
    this._mandatoryString = ' ' + this.props.Translate('mandatory');
    this._bekraftaEpostRef = React.createRef();
    this._samordningsNr = React.createRef();
    this._personNr = React.createRef();

    this.state = {
      errors:
        this.props.formType === FormType.Legitimation && this.props.isOutsideEuEesLessThanThreeYears
          ? this.getInitialErrorMap(RequiredFieldListOutSideEuEesLessThanThreeYears)
          : this.props.formType === FormType.Legitimation
          ? this.getInitialErrorMap(RequiredFieldList)
          : this.getInitialErrorMap(RequiredFieldListYrkesTitel),
      emailvalue: '',
    };
  }

  static defaultProps = {
    formLanguage: 'sv',
  } as IPersonuppgifterSectionProps;
  onEmailChanged = (field: FormField<string>) => {
    this.setState(
      {
        emailvalue: field.value,
      },

      () => {
        let clone = {
          ...this._bekraftaEpostRef.current.state,
          referenceValue: field.value,
        };
        const errors = new Map(this.state.errors);
        this.onChangeField(field, errors);
        this.onChangeField(clone, errors);
      },
    );
  };
  //Method used for setting and unsetting required on samordningsnr / personnr since one of them must be filled in.
  onPersonnummerSamordningsnrChanged = (field: FormField<string>) => {
    const errors = new Map(this.state.errors);
    //Get the clone of opposite field (if personnummer we get samordingsnr.. etc)
    const clone = {
      ...(field.fieldName === FieldName.Personnummer
        ? this._samordningsNr.current.state
        : this._personNr.current.state),
    };
    //Set current field to not required if other fields has a value
    field.isRequired = !(clone.value.length > 0);
    //Set other field to not required if current field has a value
    clone.isRequired = !(field.value.length > 0);

    //Update both fields
    this.onChangeField(clone, errors);
    this.onChangeField(field, errors);
  };

  public render() {
    const {
      Translate,
      isInEditMode,
      openWebAnsokanEpiTexts,
      formType,
      formLanguage,
      skyddadYrkestitelOpenFormEpiTexts,
      isOutsideEuEesLessThanThreeYears,
    } = this.props;
    return (
      <section aria-labelledby="personuppgifter-heading">
        <EpiserverProperty propertyName="PersonuppgifterHeading" isInEditMode={isInEditMode}>
          <h2 id="personuppgifter-heading">
            {formType == FormType.SkyddadYrkestitel
              ? skyddadYrkestitelOpenFormEpiTexts.PersonuppgifterHeading
              : openWebAnsokanEpiTexts.PersonuppgifterHeading}
          </h2>
        </EpiserverProperty>

        <EpiserverProperty propertyName="PersonuppgifterPreamble" wrappingElement="span" isInEditMode={isInEditMode}>
          <p className="preamble">
            {formType == FormType.SkyddadYrkestitel
              ? skyddadYrkestitelOpenFormEpiTexts.PersonuppgifterPreamble
              : openWebAnsokanEpiTexts.PersonuppgifterPreamble}
          </p>
        </EpiserverProperty>

        <EpiserverProperty
          propertyName="PersonuppgifterBody"
          usingDangerouslySetInnerHTML={true}
          isInEditMode={isInEditMode}>
          <div
            dangerouslySetInnerHTML={setInnerHtml(
              formType == FormType.SkyddadYrkestitel
                ? skyddadYrkestitelOpenFormEpiTexts.PersonuppgifterBody
                : openWebAnsokanEpiTexts.PersonuppgifterBody,
            )}
          />
        </EpiserverProperty>

        <div className="row m-b-2">
          <div
            className={`col-sm-6 ${
              !isOutsideEuEesLessThanThreeYears
                ? this.getErrorSiblingClass(FieldName.Tilltalsnamn, [FieldName.AllaFornamn])
                : ''
            }`}>
            <TextInput
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.AllaFornamn)}
              isRequired={true}
              label={Translate(FieldName.AllaFornamn) + this._mandatoryString}
              fieldName={FieldName.AllaFornamn}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="name"
            />
          </div>
          <div
            className={`col-sm-6 ${
              !isOutsideEuEesLessThanThreeYears
                ? this.getErrorSiblingClass(FieldName.AllaFornamn, [FieldName.Tilltalsnamn])
                : ''
            }`}>
            <TextInput
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.Tilltalsnamn)}
              isRequired={!isOutsideEuEesLessThanThreeYears}
              label={
                Translate(FieldName.Tilltalsnamn) + (!isOutsideEuEesLessThanThreeYears ? this._mandatoryString : '')
              }
              fieldName={FieldName.Tilltalsnamn}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="given-name"
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div className="col-sm-6">
            <TextInput
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.Efternamn)}
              isRequired={true}
              label={Translate(FieldName.Efternamn) + this._mandatoryString}
              fieldName={FieldName.Efternamn}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="family-name"
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div
            className={`col-sm-6 align-items-end ${this.getErrorSiblingClass(FieldName.samordningsnummerOrBirthDate, [
              FieldName.Personnummer,
            ])}`}>
            <TextInput
              onChange={this.onPersonnummerSamordningsnrChanged}
              errorMessage={this.getErrorMessage(FieldName.Personnummer)}
              isRequired={false}
              label={
                Translate(FieldName.Personnummer) + (!isOutsideEuEesLessThanThreeYears ? this._mandatoryString : '')
              }
              fieldName={FieldName.Personnummer}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              ref={this._personNr}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="off"
            />
          </div>
          <div
            className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.Personnummer, [
              FieldName.samordningsnummerOrBirthDate,
            ])}`}>
            <TextInput
              onChange={this.onPersonnummerSamordningsnrChanged}
              errorMessage={this.getErrorMessage(FieldName.samordningsnummerOrBirthDate)}
              isRequired={false}
              label={Translate(FieldName.samordningsnummerOrBirthDate) + this._mandatoryString}
              fieldName={FieldName.samordningsnummerOrBirthDate}
              ref={this._samordningsNr}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="off"
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div className="col-sm-6">
            <TextInput
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.Adress)}
              isRequired={formType === FormType.Legitimation}
              label={Translate(FieldName.Adress) + (formType === FormType.Legitimation ? this._mandatoryString : '')}
              fieldName={FieldName.Adress}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="address-line1"
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.Ort, [FieldName.Postnummer])}`}>
            <TextInput
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.Postnummer)}
              isRequired={formType === FormType.Legitimation}
              label={
                Translate(FieldName.Postnummer) + (formType === FormType.Legitimation ? this._mandatoryString : '')
              }
              fieldName={FieldName.Postnummer}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="postal-code"
            />
          </div>
          <div className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.Postnummer, [FieldName.Ort])}`}>
            <TextInput
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.Ort)}
              isRequired={formType === FormType.Legitimation}
              label={Translate(FieldName.Ort) + (formType === FormType.Legitimation ? this._mandatoryString : '')}
              fieldName={FieldName.Ort}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="address-level2"
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div className="col-sm-6">
            <TextInput
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.Land)}
              isRequired={true}
              label={Translate(FieldName.Land) + this._mandatoryString}
              fieldName={FieldName.Land}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="country-name"
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div className="col-sm-6">
            <TextInput
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.Telefon)}
              isRequired={formType === FormType.Legitimation}
              label={Translate(FieldName.Telefon) + (formType === FormType.Legitimation ? this._mandatoryString : '')}
              fieldName={FieldName.Telefon}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="tel"
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.EpostBekraftelse, [FieldName.Epost])}`}>
            <TextInput
              onChange={this.onEmailChanged}
              errorMessage={this.getErrorMessage(FieldName.Epost)}
              isRequired={true}
              disablePaste={true}
              label={Translate(FieldName.Epost) + this._mandatoryString}
              fieldName={FieldName.Epost}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="email"
            />
          </div>
          <div className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.Epost, [FieldName.EpostBekraftelse])}`}>
            <TextInput
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.EpostBekraftelse)}
              isRequired={true}
              referenceValue={this.state.emailvalue}
              ref={this._bekraftaEpostRef}
              label={Translate(FieldName.EpostBekraftelse) + this._mandatoryString}
              fieldName={FieldName.EpostBekraftelse}
              disablePaste={true}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
              autoComplete="email"
            />
          </div>
        </div>

        <RadioButtonGroup
          onChange={this.onChangeField}
          errorMessage={this.getErrorMessage(FieldName.Kon)}
          isRequired={true}
          fieldName={FieldName.Kon}
          label={Translate(FieldName.Kon) + this._mandatoryString}
          ariaLabel={Translate(FieldName.Kon)}>
          <RadioButton value={Translate('man')} label={Translate('man')} />
          <RadioButton value={Translate('kvinna')} label={Translate('kvinna')} />
          <RadioButton value={Translate('annat')} label={Translate('annat')} />
        </RadioButtonGroup>

        {formType === FormType.Legitimation && (
          <div className="eservice-form m-t-2">
            <TextInput
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.OvrigInfo)}
              classNames="height240px"
              multiline={true}
              fieldName={FieldName.OvrigInfo}
              label={Translate(FieldName.OvrigInfo)}
              maxLength={5000}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
            />
          </div>
        )}

        <hr className="formdivider" />
      </section>
    );
  }
}
